import React, { useState, useEffect, useCallback } from 'react';
import {
    Library, Upload, Cpu, CheckCircle, Edit3, ArrowUpRight,
    Users, Zap, Loader2, AlertTriangle, Info, BarChart3,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';

/**
 * CampusIntelligenceAnalyticsTab
 *
 * Operational analytics for Campus Intelligence admin actions.
 * Surfaces docs uploaded, extraction rates, records approved/promoted,
 * and context resolution usage counts.
 *
 * Powered by GET /internal/analytics/campus-intelligence
 */

const CampusIntelligenceAnalyticsTab = ({ filters = {} }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 30,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const res = await adminAnalytics.getCampusIntelligenceAnalytics(params);
            setData(res.data);
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Access denied. Admin role required.');
            } else {
                setError(err.response?.data?.detail || 'Failed to load Campus Intelligence analytics.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={centerStyle}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#8b5cf6' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading Campus Intelligence analytics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={centerStyle}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Retry
                </button>
            </div>
        );
    }

    if (!data) return null;

    const extraction = data.extraction || {};
    const records = data.records || {};
    const isNoData = data.telemetry_status === 'no_data';

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Library size={20} style={{ color: '#8b5cf6' }} />
                    Campus Intelligence Operations
                </h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Last {data.period_days || 30} days · Admin document processing & knowledge management
                </p>
            </div>

            {/* Telemetry status banner */}
            {isNoData && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '12px 16px', borderRadius: 10, marginBottom: 20,
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                }}>
                    <Info size={15} style={{ color: '#16a34a', marginTop: 1, flexShrink: 0 }} />
                    <div style={{ fontSize: 13, color: '#15803d' }}>
                        <strong>Collecting:</strong> {data.note}
                    </div>
                </div>
            )}

            {/* Document Uploads */}
            <Section title="Document Processing" icon={Upload} color="#8b5cf6">
                <Card label="Docs Uploaded" value={data.docs_uploaded ?? 0} icon={Upload} accent="#8b5cf6" />
                <Card label="Extraction Attempts" value={extraction.attempts ?? 0} icon={Cpu} accent="#6366f1" />
                <Card label="Extractions OK" value={extraction.successes ?? 0} icon={CheckCircle} accent="#10b981" />
                <Card label="Extractions Failed" value={extraction.failures ?? 0} icon={AlertTriangle}
                    accent={(extraction.failures ?? 0) > 0 ? '#ef4444' : '#6b7280'} />
                <Card
                    label="Extraction Success Rate"
                    value={extraction.success_rate_pct != null ? `${extraction.success_rate_pct}%` : '—'}
                    icon={BarChart3}
                    accent={(extraction.success_rate_pct ?? 0) >= 80 ? '#10b981' : '#f59e0b'}
                    sub={extraction.success_rate_pct == null ? 'No extractions yet' : null}
                />
            </Section>

            {/* Record Management */}
            <Section title="Record Management" icon={CheckCircle} color="#10b981">
                <Card label="Records Approved" value={records.approved ?? 0} icon={CheckCircle} accent="#10b981" />
                <Card label="Records Edited" value={records.edited ?? 0} icon={Edit3} accent="#f59e0b" />
                <Card label="Promoted to Settings" value={records.promoted_to_settings ?? 0} icon={ArrowUpRight} accent="#8b5cf6"
                    sub="Records promoted to live campus config" />
            </Section>

            {/* Usage & Adoption */}
            <Section title="Usage & Adoption" icon={Zap} color="#3b82f6">
                <Card label="Context Resolutions Used" value={data.context_resolutions_used ?? 0} icon={Zap} accent="#3b82f6"
                    sub="Times CI context surfaced to teacher workflows" />
                <Card label="Active Admin Users" value={data.unique_admin_users ?? 0} icon={Users} accent="#6366f1"
                    sub="Unique admins with CI actions this period" />
            </Section>

            {/* Doc type breakdown */}
            {data.docs_by_type && data.docs_by_type.length > 0 && (
                <Section title="Documents by Type" icon={Library} color="#8b5cf6">
                    {data.docs_by_type.map(dt => (
                        <Card key={dt.doc_type} label={dt.doc_type} value={dt.count} icon={Library} accent="#8b5cf6" />
                    ))}
                </Section>
            )}

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};


// ── Shared layout components ──────────────────────────────────────────────────

const centerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 300, flexDirection: 'column', gap: 10,
};

const Section = ({ title, icon: Icon, color = '#6366f1', children }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 12, paddingBottom: 8,
            borderBottom: `2px solid ${color}20`,
        }}>
            {Icon && <Icon size={15} style={{ color }} />}
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
        </div>
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 12,
        }}>
            {children}
        </div>
    </div>
);

const Card = ({ label, value, icon: Icon, accent = '#6366f1', sub }) => (
    <div style={{
        background: '#fff', borderRadius: 12, padding: '16px 18px',
        border: '1px solid #e5e7eb',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{label}</span>
            {Icon && <Icon size={14} style={{ color: accent }} />}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
);


export default CampusIntelligenceAnalyticsTab;
